import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from '../Navigation/Sidebar';
import MobileBottomNavigation from '../Navigation/MobileBottomNavigation';
import MobileDrawerMenu from '../Navigation/MobileDrawerMenu';
import MyProfile from '../MyProfile/MyProfile';
import IdleTimeoutManager from '../IdleTimeout/IdleTimeoutManager';
import { useIOSScrollFix } from '../../hooks/useIOSScrollFix';
import {
  LayoutRoot,
  SidebarWrapper,
  MainWrapper,
  HeaderWrapper,
  ContentWrapper,
} from '../../styles/Layout.styles';

const Layout = () => {
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef(null);
  
  // Use iOS-specific scroll fix hook
  const { forceIOSScrollReset } = useIOSScrollFix();
  
  // Additional scroll reset for content wrapper after navigation
  useEffect(() => {
    const resetContentWrapper = () => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
        contentRef.current.scrollTo && contentRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    };

    // Reset content wrapper
    resetContentWrapper();
    
    // Additional resets with timing
    const timeouts = [
      setTimeout(resetContentWrapper, 50),
      setTimeout(resetContentWrapper, 150),
      setTimeout(resetContentWrapper, 300),
    ];
    
    return () => timeouts.forEach(clearTimeout);
  }, [location.pathname]);

  // Trigger iOS scroll fix when content loads
  useEffect(() => {
    // Trigger iOS scroll fix after content loads
    const timeouts = [
      setTimeout(() => forceIOSScrollReset(), 100),
      setTimeout(() => forceIOSScrollReset(), 300),
      setTimeout(() => forceIOSScrollReset(), 600),
    ];
    
    return () => timeouts.forEach(clearTimeout);
  }, [forceIOSScrollReset]);
  
  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length === 0) return '';
    
    const title = pathSegments[pathSegments.length - 1]
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return title;
  };

  const handleProfileOpen = () => {
    setProfileOpen(true);
  };

  const handleProfileClose = () => {
    setProfileOpen(false);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <LayoutRoot>
      <SidebarWrapper>
        <Sidebar />
      </SidebarWrapper>
      <MainWrapper>
        <HeaderWrapper>
          <Header pageTitle={getPageTitle()} onProfileClick={handleProfileOpen} />
        </HeaderWrapper>
        <ContentWrapper ref={contentRef} data-testid="content-wrapper">
          <Outlet />
        </ContentWrapper>
      </MainWrapper>
      
      {/* Mobile Navigation */}
      <MobileBottomNavigation 
        onMenuClick={handleMobileMenuOpen} 
        sx={{
          '@media (max-width:900px) and (orientation: landscape)': {
            display: 'none', // Hide bottom navigation in landscape mode
          }
        }}
      />
      <MobileDrawerMenu 
        open={mobileMenuOpen}
        onOpen={handleMobileMenuOpen}
        onClose={handleMobileMenuClose}
      />
      
      <MyProfile 
        open={profileOpen} 
        onClose={handleProfileClose}
      />
      <IdleTimeoutManager />
    </LayoutRoot>
  );
};

export default Layout;
